
import React from 'react';
import { CheckCircle, AlertCircle, Loader2, WifiOff } from 'lucide-react';
import { useInitialSync } from '@/hooks/useInitialSync';
import { useOfflineStatus } from '@/hooks/useOfflineStatus';
import { useThemeConfig } from '@/components/ui/theme-config-provider';

interface SyncStatusIndicatorProps {
  className?: string;
  showDetails?: boolean;
}

export const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({
  className = "",
  showDetails = false
}) => {
  const { syncStatus, isSyncing, lastSyncTime } = useInitialSync();
  const { isOnline } = useOfflineStatus();
  const { theme } = useThemeConfig();

  const formatLastSync = (timestamp: number | null) => {
    if (!timestamp) return 'Mai';
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (minutes < 1) return 'Appena ora';
    if (minutes < 60) return `${minutes} min fa`;
    if (hours < 24) return `${hours} ore fa`;
    return new Date(timestamp).toLocaleDateString();
  };

  if (!isOnline) {
    return (
      <div
        className={`flex items-center gap-2 ${className}`}
        style={{ color: theme.warningColor }}
      >
        <WifiOff size={16} />
        <span className="text-xs">Modalità offline</span>
      </div>
    );
  }

  if (isSyncing) {
    return (
      <div
        className={`flex items-center gap-2 ${className}`}
        style={{ color: theme.primaryColor }}
      >
        <Loader2 size={16} className="animate-spin" />
        <span className="text-xs">Sincronizzazione...</span>
      </div>
    );
  }

  if (syncStatus?.isComplete) {
    return (
      <div
        className={`flex items-center gap-2 ${className}`}
        style={{ color: theme.successColor }}
      >
        <CheckCircle size={16} />
        <div className="text-xs">
          <div>Sincronizzato</div>
          {showDetails && (
            <div style={{ color: theme.textColor }}>
              {formatLastSync(lastSyncTime)}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (syncStatus && !syncStatus.isComplete) {
    return (
      <div
        className={`flex items-center gap-2 ${className}`}
        style={{ color: theme.warningColor }}
      >
        <AlertCircle size={16} />
        <div className="text-xs">
          <div>Sync parziale</div>
          {showDetails && (
            <div style={{ color: theme.textColor }}>
              {formatLastSync(lastSyncTime)}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-2 ${className}`}
      style={{ color: theme.textColor }}
    >
      <AlertCircle size={16} />
      <span className="text-xs">Non sincronizzato</span>
    </div>
  );
};

export default SyncStatusIndicator;
